// @givmo/board — the person's own annual disclosure for one year (CBG-YR2-WEB2; frames d2-fresh,
// d3b-rolled-opens, d3-rolled, d4-review, d5-signed), at `/director/disclosures/annual?org=&cycle=`
// (`&mode=correct` to correct answers already given). The request mail links here (the backend's
// `BOARD_DIRECTOR_ANNUAL_DISCLOSURE_PATH`).
//
// ══ THE QUESTIONS, THEN A REVIEW, THEN ONE SIGNATURE ════════════════════════════════════════════
//
// The page renders the year's questions exactly as the backend asks them of this person (D2), with
// last year's answers filled in and nothing to tap to keep one (Jonathan's answer to question 4). The
// review lists what changed from last year in the redline's own words and marks, then the sentence the
// person signs and Sign. Sign is a signature-class act through the shared gate (`useDirectorAct`, the
// `coi_questionnaire` row): an open window signs at once; a shut one brings the "Confirm it's you"
// sheet up over the review, and the answers are sent once after it exactly as reviewed. The signed
// answers are the record: the page then shows it as signed, with the person's copy and their changes as
// the backend's PDFs, and a correction — never an edit — as the way to fix one.
//
// ══ NOTHING HALF-SUBMITTED LEAVES THE TAB ═══════════════════════════════════════════════════════
//
// Unsent answers live only in this tab (`director-coi.ts`, `director-drafts.ts`): there are no
// server-side drafts, and no officer ever sees that someone started. Nothing here tells the person
// what an answer means; answering decides nothing (the "How this works" lines say so once).

import { useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import {
  firstErrorCode,
  isGivmoError,
  type BoardCoiAnswerOut,
  type BoardCoiDirectorCycleOut,
  type BoardCoiPreviousAnswerOut,
  type BoardCoiPreviousOut,
  type BoardCoiQuestionAskedOut,
  type BoardCoiQuestionnaireForMeOut,
  type BoardCoiResponseOut,
} from '@givmo/api-client';
import {
  Button,
  Card,
  CardBody,
  ErrorMessage,
  Icon,
  Skeleton,
  cn,
  testIdProp,
} from '@givmo/design-system';
import { Link, useNavigate, useSearch } from '../../board-routing';
import { BOARD_DIRECTOR_PAGES } from '../../board-routes';
import { useBoardDirectorOperations } from '../../board-runtime';
import { Fingerprint } from '../../components/fingerprint';
import { HowThisWorks } from '../../components/fine-print';
import { displayDate } from '../../lib/display';
import {
  directorBoardIdentity,
  directorBoardSearch,
  useDirectorBoardAddress,
} from '../../director/director-board';
import { useDirectorDraft, type DirectorDraftScope } from '../../director/director-drafts';
import { useBoardDirectorHost } from '../../director/director-host';
import { boardDirectorMeKey } from '../../director/director-me';
import { DIRECTOR_REFUSALS, isCommitteeOnly } from '../../director/director-wire';
import { useDirectorFailureWords } from '../../director/access-refusal';
import {
  DirectorReadRefusal,
  isSigningRefusal,
  useDirectorReadsMayFire,
} from '../../director/read-gate';
import {
  AnswerForm,
  FormActions,
  answersIn,
  draftProblems,
  initialDrafts,
  previousAnswerOf,
  useAnswerDrafts,
  useChangedQuestions,
  type AnswerDraft,
  type AnswerDrafts,
  type EntryDraft,
} from '../conflicts/coi-answer-form';
import {
  ENTRY_FIELDS,
  KIND_ACKNOWLEDGMENT,
  KIND_TEXT,
  PART_POLICY,
  answerWord,
  decodeResponse,
  longDay,
  moment,
  todayIso,
  type EntryLike,
} from '../conflicts/coi-decode';
import { CleanCopy, type CleanCopySource } from '../conflicts/coi-document';
import { ChangeLabel, DELETED, EntryLine, INSERTED } from '../conflicts/coi-parts';
import { coiWords as w, ownFactorWords } from '../conflicts/coi-words';
import { DirectorSetupOfferPage, actMessage, useDirectorAct } from './director-act';
import { DirectorBoardContent } from './director-board-content';
import { DirectorMovePointer } from './move-pointer';
import {
  applyQuestionnaireDraft,
  ownCoiKeys,
  questionnaireDraftFields,
  useOwnCoiQuestionnaire,
  useOwnCoiResponse,
} from './director-coi';
import { DirectorBoardOnlyPage } from './director-board-only-page';
import { directorPagesWords as dp } from './director-pages-words';
import { DirectorShell } from './director-shell';
import { useOwnPdf } from './director-year-card';

const TITLE = 'font-sans text-s18 font-w700 tracking-givmo text-text-strong';
const LEAD = 'font-sans text-s15 font-w400 leading-h15 tracking-givmo text-text-strong';
const BODY = 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary';
const LINK =
  'font-sans text-s14 font-w600 tracking-givmo text-brand-600 outline-none hover:text-brand-700 focus-visible:underline';

/** The search value, when it is a non-empty string. */
function textParam(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

export function DirectorAnnualDisclosurePage() {
  const { board } = useDirectorBoardAddress();
  // CBG-YR6-OUTSIDE (review-215's F5): The year's conflict-of-interest questionnaire is the board's alone; a seat for someone who isn't
  // on the board, here by address, meets the one not-found and nothing of the board's.
  if (isCommitteeOnly(board))
    return <DirectorBoardOnlyPage testId="page-director-annual-disclosure" />;
  return <DirectorAnnualDisclosurePageForTheBoard />;
}

function DirectorAnnualDisclosurePageForTheBoard() {
  const search = useSearch();
  const navigate = useNavigate();
  const { orgRef, board, notYours } = useDirectorBoardAddress();
  const mayFire = useDirectorReadsMayFire(orgRef);
  const cycleId = textParam(search.cycle);
  const correcting = search.mode === 'correct';
  // A year's "Your answers" link opens them (`&view=answers`); a signature lands on the card alone.
  const answersOpen = search.view === 'answers';

  const questionnaireQuery = useOwnCoiQuestionnaire(orgRef, cycleId, mayFire);
  const questionnaire = questionnaireQuery.data ?? null;
  const cycle = questionnaire?.cycle ?? null;
  const answered = cycle?.response ?? null;
  const responseQuery = useOwnCoiResponse(orgRef, cycleId, mayFire && answered !== null);
  // The response this visit signed, shown at once (the reads refresh behind it).
  const [signed, setSigned] = useState<BoardCoiResponseOut | null>(null);
  const response = signed ?? responseQuery.data ?? null;

  const organization =
    // The organization's name where the year's and the board's are missing (approved, CBG-YR2-COPY1).
    cycle?.organization_name?.trim() || board?.organization_name?.trim() || 'your organization';
  const identity = directorBoardIdentity(board);
  const canSign = board?.another_account_required !== true;
  const boardSearch = orgRef === null ? undefined : directorBoardSearch(orgRef);

  const leaveCorrection = () =>
    void navigate({
      to: BOARD_DIRECTOR_PAGES.annualDisclosure,
      search: { ...(boardSearch ?? {}), ...(cycleId ? { cycle: cycleId } : {}) },
      replace: true,
    });

  const backToDisclosures = (
    <Link
      to={BOARD_DIRECTOR_PAGES.disclosures}
      search={boardSearch}
      exact
      className={LINK}
      {...testIdProp('director-coi-back')}
    >
      {/* CBG-UX10 (D-CBG-40): the page's name, the officer's, everywhere. */}
      {dp.conflictsTitle}
    </Link>
  );

  const unavailable = (
    <Card testId="director-coi-unavailable">
      <CardBody className="flex flex-col items-start gap-12">
        <p className={BODY}>{w.questionnaireUnavailable}</p>
        {backToDisclosures}
      </CardBody>
    </Card>
  );

  let body: ReactNode;
  if (cycleId === null) {
    body = unavailable;
  } else if (questionnaireQuery.isPending) {
    body = (
      <div className="flex flex-col gap-12" data-testid="director-coi-loading">
        <Skeleton className="h-84 w-full rounded-lg" />
        <Skeleton className="h-90 w-full rounded-lg" />
      </div>
    );
  } else if (questionnaireQuery.isError && isSigningRefusal(questionnaireQuery.error)) {
    body = null;
  } else if (questionnaire === null || cycle === null) {
    body = unavailable;
  } else if (response !== null && !(correcting && signed === null)) {
    body = (
      <SignedView
        orgRef={orgRef ?? ''}
        cycle={cycle}
        response={response}
        organization={organization}
        canCorrect={canSign && cycle.status !== 'cancelled'}
        correctSearch={{ ...(boardSearch ?? {}), cycle: cycle.cycle_id, mode: 'correct' }}
        answersOpen={answersOpen}
      />
    );
  } else if (answered !== null) {
    // Answered, and the answers are on their way (or a correction waits for them).
    body = responseQuery.isError ? (
      unavailable
    ) : (
      <Skeleton className="h-90 w-full rounded-lg" testId="director-coi-response-loading" />
    );
  } else if (cycle.status === 'cancelled') {
    body = (
      <Ended words={w.yearWithdrawn} back={backToDisclosures} testId="director-coi-withdrawn" />
    );
  } else if (!cycle.accepts_answers) {
    body = (
      <Ended words={w.yearClosedNoAnswer} back={backToDisclosures} testId="director-coi-closed" />
    );
  } else if (!canSign && orgRef !== null) {
    // CBG-SEC2-L4-MOVE: an account another organization's sign-in lock binds signs nothing here;
    // the move's pointer says why, as on Conflicts of interest.
    body = (
      <Card testId="director-coi-move">
        <CardBody>
          <DirectorMovePointer orgRef={orgRef} blocks="sign" testId="director-coi-move-pointer" />
        </CardBody>
      </Card>
    );
  } else {
    body = null;
  }

  // The form: a first answer while the year takes one, or a correction of the answers given.
  const correctingFrom = correcting && answered !== null && signed === null ? response : null;
  // Once this visit has signed, the signed answers show, whatever a read still on its way says.
  const showForm =
    signed === null &&
    cycleId !== null &&
    questionnaire !== null &&
    cycle !== null &&
    canSign &&
    ((answered === null && cycle.status !== 'cancelled' && cycle.accepts_answers) ||
      correctingFrom !== null);

  return (
    <DirectorShell
      title={
        // The title waits only while the questionnaire is on its way: once it settles with no year to
        // name, the page says what it is, never a loading bar left for good (givmo-design §5).
        cycle ? (
          w.yourYear(cycle.label)
        ) : cycleId !== null && questionnaireQuery.isPending ? (
          <Skeleton className="h-28 w-[320px] max-w-full" />
        ) : (
          w.yourAnnualDisclosure
        )
      }
      description={
        cycle
          ? cycle.joined_after_close === true
            ? w.forBoardJoinedAfterClose(organization)
            : w.forBoard(organization, longDay(cycle.due_on), cycle.period_label)
          : undefined
      }
      identity={identity}
      testId="page-director-annual-disclosure"
    >
      <DirectorBoardContent orgRef={orgRef} notYours={notYours}>
        <DirectorReadRefusal reads={[questionnaireQuery]} testId="director-coi-read-step-up" />
        {showForm && questionnaire !== null && cycle !== null && orgRef !== null ? (
          <Questionnaire
            key={`${cycle.cycle_id}:${correctingFrom?.record_id ?? 'first'}`}
            orgRef={orgRef}
            seatId={board?.board_member_id ?? null}
            cycle={cycle}
            questionnaire={questionnaire}
            correctingFrom={correctingFrom}
            organization={organization}
            identity={identity}
            onSigned={(saved) => {
              setSigned(saved);
              if (correcting) leaveCorrection();
              window.scrollTo({ top: 0 });
            }}
          />
        ) : (
          body
        )}
      </DirectorBoardContent>
    </DirectorShell>
  );
}

/** A year that takes no answer now: one quiet line, and the way back. */
function Ended({ words, back, testId }: { words: string; back: ReactNode; testId: string }) {
  return (
    <Card testId={testId}>
      <CardBody className="flex flex-col items-start gap-12">
        <p className={BODY}>{words}</p>
        {back}
      </CardBody>
    </Card>
  );
}

// ── The questions, the review, Sign ──────────────────────────────────────────────────────────────

/** Last year's answers as the read serves them, or — for a correction — the answers corrected. */
function startingPoint(
  questionnaire: BoardCoiQuestionnaireForMeOut,
  correctingFrom: BoardCoiResponseOut | null,
): BoardCoiPreviousOut | null {
  if (correctingFrom !== null) return null;
  return questionnaire.previous ?? null;
}

function Questionnaire({
  orgRef,
  seatId,
  cycle,
  questionnaire,
  correctingFrom,
  organization,
  identity,
  onSigned,
}: {
  orgRef: string;
  seatId: string | null;
  cycle: BoardCoiDirectorCycleOut;
  questionnaire: BoardCoiQuestionnaireForMeOut;
  correctingFrom: BoardCoiResponseOut | null;
  organization: string;
  identity: string | undefined;
  onSigned: (saved: BoardCoiResponseOut | null) => void;
}) {
  const operations = useBoardDirectorOperations();
  const queryClient = useQueryClient();
  const { account } = useBoardDirectorHost();
  const questions = questionnaire.questions;
  const previous = startingPoint(questionnaire, correctingFrom);
  const correcting = correctingFrom !== null;
  const initial = useMemo(
    () => initialDrafts(questions, previous, correctingFrom?.answers),
    [questions, previous, correctingFrom],
  );
  const { drafts, setDrafts, edit } = useAnswerDrafts(() => initial);
  // CBG-FIX104: the access refusal in the words of the seat `/me` listed here.
  const failureWords = useDirectorFailureWords();
  const [step, setStep] = useState<'questions' | 'review'>('questions');
  const [tried, setTried] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allShown, setAllShown] = useState(false);

  // What the person typed or changed, kept in this tab across a forced sign-out.
  const draftScope = useMemo<DirectorDraftScope | null>(
    () =>
      account?.uid !== undefined && seatId !== null
        ? {
            uid: account.uid,
            orgRef,
            seatId,
            surface: 'coi-questionnaire',
            cycleId: correcting ? `${cycle.cycle_id}.correct` : cycle.cycle_id,
          }
        : null,
    [account?.uid, orgRef, seatId, cycle.cycle_id, correcting],
  );
  const fields = questionnaireDraftFields(questions, previous, initial, drafts);
  const draftWriter = useDirectorDraft(draftScope, fields, (kept) => {
    // Only into a form nothing has been changed in yet.
    if (Object.keys(fields).length > 0) return;
    setDrafts(applyQuestionnaireDraft(questions, previous, initial, kept));
  });

  const problems = draftProblems(questions, drafts);
  const shownProblems = tried ? problems : {};
  const changed = useChangedQuestions(questions, drafts, previous);
  const statement = questions.find((question) => question.part === PART_POLICY) ?? null;
  const statementState: 'ticked' | 'unticked' | 'none' =
    statement === null ? 'none' : drafts[statement.id]?.value === true ? 'ticked' : 'unticked';

  const record = useDirectorAct<{ data?: unknown }>('coi_questionnaire', {
    onDone: async (value) => {
      draftWriter.discard();
      const saved = decodeResponse(value?.data);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ownCoiKeys(orgRef).root }),
        queryClient.invalidateQueries({ queryKey: ['board-director-disclosures', orgRef] }),
        queryClient.invalidateQueries({ queryKey: ['board-director-attention', orgRef] }),
      ]);
      onSigned(saved);
    },
    onFailed: (caught) => {
      if (isGivmoError(caught) && firstErrorCode(caught) === DIRECTOR_REFUSALS.intentChanged) {
        setError(w.wordsChanged);
        void queryClient.invalidateQueries({ queryKey: boardDirectorMeKey() });
        return;
      }
      setError(failureWords(caught, w.couldNotSave));
    },
  });

  const review = () => {
    setTried(true);
    if (Object.keys(problems).length > 0) {
      const first = questions.find((question) => problems[question.id] !== undefined);
      if (first)
        document
          .querySelector(`[data-testid="director-coi-form-${first.id}"]`)
          ?.scrollIntoView({ block: 'center' });
      return;
    }
    setError(null);
    setStep('review');
    window.scrollTo({ top: 0 });
  };

  const sign = () => {
    setError(null);
    // The thunk holds the answers as reviewed: a confirm-and-retry sends exactly these.
    const body = {
      signed_on: todayIso(),
      answers: answersIn(questions, drafts),
      ...(correctingFrom !== null ? { corrects_record_id: correctingFrom.record_id } : {}),
    };
    record.begin(
      (intentStatementId) => () =>
        operations.directorSubmitOwnBoardCoiResponse({
          path: { org_ref: orgRef, cycle_id: cycle.cycle_id },
          body: { ...body, intent_statement_id: intentStatementId },
          throwOnError: true,
        }),
    );
  };

  if (record.offering) {
    return (
      <DirectorSetupOfferPage
        variant="from-sign"
        identity={identity}
        onSettled={record.settleOffer}
      />
    );
  }

  const shownError = actMessage(record, error);
  const policy = questionnaire.policy ?? null;
  const intro = correcting
    ? w.correctingIntro
    : previous !== null
      ? w.rolledIntro
      : w.freshIntro(organization);

  if (step === 'review') {
    const answersShown: CleanCopySource = {
      answers: draftAnswers(questions, drafts),
      policy,
      received_after_close: false,
      cycle_label: cycle.label,
    };
    const listChanges = previous !== null && !correcting;
    return (
      <div className="flex flex-col gap-24" data-testid="director-coi-review-step">
        <Card testId="director-coi-review">
          <CardBody className="flex flex-col gap-12">
            <h2 className={TITLE}>{w.checkYourAnswers}</h2>
            <p className={BODY} data-testid="director-coi-review-summary">
              {listChanges
                ? w.reviewSummary(changed.length, previous.cycle_label ?? '', statementState)
                : w.reviewFirst(statementState)}
            </p>
            {listChanges ? (
              <>
                <ul className="flex flex-col gap-12">
                  {changed.map((question) => (
                    <ReviewChange
                      key={question.id}
                      question={question}
                      draft={drafts[question.id]}
                      before={previousAnswerOf(previous, question.id)}
                    />
                  ))}
                </ul>
                <button
                  type="button"
                  className={cn(LINK, 'self-start')}
                  onClick={() => setAllShown((shown) => !shown)}
                  aria-expanded={allShown}
                  data-testid="director-coi-review-all"
                >
                  {w.seeAllAnswers}
                </button>
                {allShown ? (
                  <CleanCopy response={answersShown} testId="director-coi-review-answers" />
                ) : null}
              </>
            ) : (
              <CleanCopy response={answersShown} testId="director-coi-review-answers" />
            )}
          </CardBody>
        </Card>

        <Card testId="director-coi-sign">
          <CardBody className="flex flex-col gap-14">
            {/* The words this act affirms, from `/me` (contract §7), above the act. */}
            <p
              className="font-sans text-s15 font-w600 leading-h15 tracking-givmo text-text-strong"
              data-testid="director-coi-intent"
            >
              {record.intent?.text ?? questionnaire.intent_statement.text}
            </p>
            <p className={BODY}>
              {correcting
                ? w.signingAsksCorrection(organization, cycle.label)
                : w.signingAsks(organization, cycle.label)}
            </p>
            {/* CBG-FIX127: the pause's line (S1) before Sign, until a press says it as the error. */}
            {record.pauseLine !== null && !record.paused ? (
              <p
                className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary"
                data-testid="director-coi-pause-line"
              >
                {record.pauseLine}
              </p>
            ) : null}
            {shownError !== null ? (
              <ErrorMessage testId="director-coi-error">{shownError}</ErrorMessage>
            ) : null}
            <div className="flex flex-wrap justify-end gap-10">
              <Button
                appearance="saas"
                tone="secondary"
                disabled={record.pending}
                onClick={() => {
                  setStep('questions');
                  window.scrollTo({ top: 0 });
                }}
                testId="director-coi-back-to-questions"
              >
                {w.backToQuestions}
              </Button>
              <Button
                appearance="saas"
                tone="primary"
                loading={record.working}
                disabled={record.pending || record.intent === null}
                onClick={sign}
                testId="director-coi-sign-button"
              >
                {w.sign}
              </Button>
            </div>
            {/* The sheet, inside the page: the answers stay where they are behind it. */}
            {record.dialog}
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-24" data-testid="director-coi-questions">
      <Card testId="director-coi-intro">
        <CardBody className="flex flex-col gap-8">
          <p className={LEAD}>{intro}</p>
          <p className={BODY}>
            {correcting ? null : <>{w.becomeDisclosure} </>}
            <HowThisWorks testId="director-coi-how" lines={w.directorHow} />
          </p>
        </CardBody>
      </Card>

      {(questionnaire.disclosures_since ?? []).length > 0 && !correcting ? (
        <Card testId="director-coi-since">
          <CardBody className="flex flex-col gap-10">
            <p className={BODY}>{w.alsoTold}</p>
            <ul className="flex flex-col gap-8">
              {(questionnaire.disclosures_since ?? []).map((single) => (
                <li
                  key={single.record_id}
                  className="flex flex-col gap-2 border-l-2 border-border-subtle pl-12"
                >
                  {single.counterparty_name ? (
                    <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                      {single.counterparty_name}
                    </span>
                  ) : null}
                  <span className="whitespace-pre-wrap font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
                    {single.interest_summary}
                  </span>
                  <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
                    {w.disclosedOnItsOwn(displayDate(single.disclosed_on))}
                  </span>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      ) : null}

      <AnswerForm
        questions={questions}
        previous={previous}
        drafts={drafts}
        edit={edit}
        policy={policy}
        label={cycle.label}
        period={cycle.period_label}
        readPolicy={
          policy !== null ? (
            <Link
              to={BOARD_DIRECTOR_PAGES.record}
              params={{ recordId: policy.record_id }}
              search={directorBoardSearch(orgRef)}
              className={LINK}
              {...testIdProp('director-coi-read-policy')}
            >
              {w.readIt}
            </Link>
          ) : undefined
        }
        problems={shownProblems}
        carried={previous !== null}
        detailsAlways={correcting}
        testId="director-coi-form"
      />

      <FormActions>
        <Button
          appearance="saas"
          tone="primary"
          onClick={review}
          testId="director-coi-review-button"
        >
          {w.reviewAndSign}
        </Button>
      </FormActions>
    </div>
  );
}

/** The person's answers, before they sign, in the clean copy's own shape (for the review). */
function draftAnswers(
  questions: readonly BoardCoiQuestionAskedOut[],
  drafts: AnswerDrafts,
): BoardCoiAnswerOut[] {
  const sent = answersIn(questions, drafts);
  return questions.map((question, index) => {
    const answer = sent[index];
    const draft = drafts[question.id];
    return {
      question_id: question.id,
      kind: question.kind,
      part: question.part,
      text_as_asked: question.text_as_asked,
      points_as_asked: question.points_as_asked ?? [],
      value: answer?.value ?? null,
      // Only an answer that names entries carries them (a "No" names none). Display only: an entry
      // the server hasn't keyed yet is keyed here by its place on the screen.
      entries:
        answer?.entries === undefined
          ? []
          : (draft?.entries ?? []).map((entry) => ({
              ...cleanFields(entry),
              key: entry.key ?? entry.localId,
            })),
      details: answer?.details ?? null,
    };
  });
}

function cleanFields(entry: EntryDraft): EntryLike {
  const fields: EntryLike = {};
  for (const field of [...ENTRY_FIELDS, 'details'] as const) {
    const value = entry.fields[field]?.trim();
    if (value) fields[field] = value;
  }
  return fields;
}

/** Whether a kept entry still says what last year's said. */
function sameAs(entry: EntryDraft, before: EntryLike | undefined): boolean {
  if (before === undefined) return false;
  return [...ENTRY_FIELDS, 'details' as const].every(
    (field) => (entry.fields[field] ?? '').trim() === (before[field] ?? '').trim(),
  );
}

/**
 * One changed answer in the review (d4-review): the question, the answer's two words struck and
 * underlined, and the entries and note it removed, changed or added, in the redline's words, marks and
 * order (o7-changes).
 */
function ReviewChange({
  question,
  draft,
  before,
}: {
  question: BoardCoiQuestionAskedOut;
  draft: AnswerDraft | undefined;
  before: BoardCoiPreviousAnswerOut | null;
}) {
  if (draft === undefined || before === null) return null;
  const textAnswer = question.kind === KIND_TEXT;
  const nowWord = answerWord(draft.value, question.kind);
  const thenWord = answerWord(before.value, question.kind);
  const beforeEntries = before.entries ?? [];
  const added = draft.entries.filter((entry) => entry.key === null);
  const edited = draft.entries.filter(
    (entry) =>
      entry.key !== null &&
      !sameAs(
        entry,
        beforeEntries.find((old) => old.key === entry.key),
      ),
  );
  const removed = beforeEntries.filter(
    (old) => !draft.entries.some((entry) => entry.key === old.key),
  );
  const noteNow = textAnswer ? '' : draft.details.trim();
  const noteThen = textAnswer ? '' : (before.details ?? '').trim();
  const noteChanged = noteNow !== noteThen;

  return (
    <li
      className="flex flex-col gap-6 border-l-2 border-border-subtle pl-12"
      data-testid={`director-coi-change-${question.id}`}
    >
      <span className={BODY}>{question.text_as_asked}</span>
      {nowWord !== thenWord && question.kind !== KIND_ACKNOWLEDGMENT ? (
        <p className="flex flex-wrap items-baseline gap-8 font-sans text-s15 tracking-givmo text-text-strong">
          <span className={cn('font-w600', DELETED)}>{thenWord}</span>
          <span className={cn('font-w600', INSERTED)}>{nowWord}</span>
        </p>
      ) : null}
      {removed.length > 0 || (noteChanged && noteThen.length > 0) ? (
        <>
          <ChangeLabel>{w.removed}</ChangeLabel>
          {removed.map((entry) => (
            <EntryLine key={entry.key} entry={entry} mark="del" />
          ))}
          {noteChanged && noteThen.length > 0 ? (
            <p className={cn(BODY, 'whitespace-pre-wrap', DELETED)}>{noteThen}</p>
          ) : null}
        </>
      ) : null}
      {edited.length > 0 ? (
        <>
          <ChangeLabel>{w.changed}</ChangeLabel>
          {edited.map((entry) => (
            <div key={entry.localId} className="flex flex-col gap-4">
              <EntryLine
                entry={beforeEntries.find((old) => old.key === entry.key) ?? {}}
                mark="del"
              />
              <EntryLine entry={cleanFields(entry)} mark="ins" />
            </div>
          ))}
        </>
      ) : null}
      {added.length > 0 || (noteChanged && noteNow.length > 0) ? (
        <>
          <ChangeLabel>{w.added}</ChangeLabel>
          {added.map((entry) => (
            <EntryLine key={entry.localId} entry={cleanFields(entry)} mark="ins" />
          ))}
          {noteChanged && noteNow.length > 0 ? (
            <p className={cn(BODY, 'whitespace-pre-wrap', INSERTED)}>{noteNow}</p>
          ) : null}
        </>
      ) : null}
    </li>
  );
}

// ── Signed ───────────────────────────────────────────────────────────────────────────────────────

/**
 * The answers in the board's records (d5-signed): how and when, the fingerprint, both PDFs, and the
 * answers themselves behind "See all your answers" (open when the person came to read them).
 */
function SignedView({
  orgRef,
  cycle,
  response,
  organization,
  canCorrect,
  correctSearch,
  answersOpen,
}: {
  orgRef: string;
  cycle: BoardCoiDirectorCycleOut;
  response: BoardCoiResponseOut;
  organization: string;
  canCorrect: boolean;
  correctSearch: Record<string, string>;
  answersOpen: boolean;
}) {
  const download = useOwnPdf(orgRef);
  const [shown, setShown] = useState(answersOpen);
  const paper = response.capture.road === 'paper';
  const previousLabel = response.previous?.cycle_label ?? null;
  const pdfCycle = { cycle_id: cycle.cycle_id, label: cycle.label };
  return (
    <div className="flex flex-col gap-24" data-testid="director-coi-signed-step">
      <Card testId="director-coi-signed">
        <CardBody className="flex flex-col gap-14">
          <div className="flex items-center gap-12">
            <span className="flex size-28 shrink-0 items-center justify-center rounded-full bg-success-50 text-success-600">
              <Icon icon={Check} size={16} aria-hidden />
            </span>
            <h2 className={TITLE}>{paper ? w.recorded : w.signed}</h2>
          </div>
          <p className={LEAD} data-testid="director-coi-signed-line">
            {paper
              ? w.recordedLine(cycle.label, organization, displayDate(response.signed_on))
              : w.signedLine(
                  cycle.label,
                  organization,
                  // When the answers were filed: the window may have opened up to an hour earlier.
                  moment(response.recorded_at),
                  ownFactorWords(response.capture.method),
                )}
          </p>
          {response.content_hash ? (
            <Fingerprint value={response.content_hash} testId="director-coi-signed-fingerprint" />
          ) : null}
          <div className="flex flex-wrap gap-10">
            <Button
              appearance="saas"
              tone="secondary"
              onClick={() => void download(pdfCycle, 'clean')}
              testId="director-coi-signed-copy"
            >
              {w.yourCopy}
            </Button>
            {previousLabel !== null ? (
              <Button
                appearance="saas"
                tone="secondary"
                onClick={() => void download(pdfCycle, 'changes')}
                testId="director-coi-signed-changes"
              >
                {w.yourChanges(previousLabel)}
              </Button>
            ) : null}
          </div>
        </CardBody>
      </Card>

      {canCorrect ? (
        <p className={BODY} data-testid="director-coi-correct-line">
          {w.somethingWrong}{' '}
          <Link
            to={BOARD_DIRECTOR_PAGES.annualDisclosure}
            search={correctSearch}
            className={LINK}
            {...testIdProp('director-coi-correct')}
          >
            {w.correctYourAnswers}
          </Link>
          . {w.correctionNote}
        </p>
      ) : null}

      <button
        type="button"
        className={cn(LINK, 'self-start')}
        onClick={() => setShown((open) => !open)}
        aria-expanded={shown}
        data-testid="director-coi-own-answers-toggle"
      >
        {w.seeAllAnswers}
      </button>
      {shown ? (
        <Card testId="director-coi-own-copy">
          <CardBody className="p-24 max-sm:p-16">
            <CleanCopy response={response} testId="director-coi-own-answers" />
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
}
